import { useState, useEffect } from 'react'

const EmployeeQuestionsQuestion = ({ requestData, formData, setFormData }) => {
    const [questionState, setQuestionState] = useState({
        'Is the employee fit for their normal hours and duties required by their post? If not, when will they be able to return to work/return to normal hours/duties or alternative work?': { selected: false },
        'What if any work adjustments may facilitate a return to the substantive role?': { selected: false },
        'If the substantive role cannot be adjusted sufficiently what temporary alternative duties is the employee fit to undertake safely?': { selected: false },
        'Is there an underlying medical condition affecting their ability to work?': { selected: false },
        'Has the employee accessed appropriate treatment/support to support their health, wellbeing and attendance?': { selected: false },
        'Is there any additional help or treatment recommended to support the employee?': { selected: false },
        'What lifestyle adjustments if any are recommended for the employee to support their ongoing health, wellbeing and work attendance?': { selected: false },
        'Is the employee fit for work in any capacity? If not when is the employee likely to be fit to return to work?': { selected: false },
        'Is this employee likely to be able to provide regular and effective service in the future?': { selected: false },
        'If the employee is unlikely to return to their substantive post for health reasons but can work in an alternative role is permanent redeployment recommended?': { selected: false },
        'If the employee is unlikely to return to their substantive post for health reasons is the employee a candidate for ill health retirement application?': { selected: false },
        'In your view, is the employee likely to be covered under the disability provision of the Equality Act 2010? Occupational health can provide guidance only on this question.': { selected: false },
        'Is the employee fit to attend a formal meeting (i.e. disciplinary)?': { selected: false },
        'Other – maximum two extra questions permitted. Please provide detail below. Any additional questions will be chargeable.': { selected: false, text_response: '' }
    })

    useEffect(() => {
        if (requestData && !requestData.v2) {
        }
    }, [requestData])

    useEffect(() => {
        const existingResponses = formData.form_responses?.question6?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(response => {
                if (newState[response.answer_text]) {
                    newState[response.answer_text].selected = true;

                    if (response.text_response) {
                        newState[response.answer_text].text_response = response.text_response;
                    }
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question6?.responses])

    const getSelectedCount = () => {
        return Object.values(questionState).filter(q => q.selected).length;
    };

    const isAtLimit = () => {
        return getSelectedCount() >= 3;
    };

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null) => {
        if (checkedValue && !hasResponse(responseKey) && isAtLimit()) {
            return;
        }

        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                newState[responseKey] = {
                    ...newState[responseKey],
                    [dataKey]: value
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question6?.responses || [];

            if (dataKey) {
                const updatedResponses = currentResponses.map(response =>
                    response.answer_text === responseKey
                        ? { ...response, [dataKey]: value }
                        : response
                );

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question6: {
                            question_title: "What questions would you want us to ask the employee?",
                            responses: updatedResponses
                        }
                    }
                };
            } else {
                let updatedResponses = [...currentResponses];

                if (checkedValue) {
                    if (!updatedResponses.find(r => r.answer_text === responseKey)) {
                        updatedResponses.push({
                            answer_text: responseKey,
                            sub_responses: []
                        });
                    }
                } else {
                    updatedResponses = updatedResponses.filter(r => r.answer_text !== responseKey);
                }

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question6: {
                            question_title: "What questions would you want us to ask the employee?",
                            responses: updatedResponses
                        }
                    }
                };
            }
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey) => {
        return questionState[responseKey]?.[dataKey] || '';
    };

    const questions = [
        'Is the employee fit for their normal hours and duties required by their post? If not, when will they be able to return to work/return to normal hours/duties or alternative work?',
        'What if any work adjustments may facilitate a return to the substantive role?',
        'If the substantive role cannot be adjusted sufficiently what temporary alternative duties is the employee fit to undertake safely?',
        'Is there an underlying medical condition affecting their ability to work?',
        'Has the employee accessed appropriate treatment/support to support their health, wellbeing and attendance?',
        'Is there any additional help or treatment recommended to support the employee?',
        'What lifestyle adjustments if any are recommended for the employee to support their ongoing health, wellbeing and work attendance?',
        'Is the employee fit for work in any capacity? If not when is the employee likely to be fit to return to work?',
        'Is this employee likely to be able to provide regular and effective service in the future?',
        'If the employee is unlikely to return to their substantive post for health reasons but can work in an alternative role is permanent redeployment recommended?',
        'If the employee is unlikely to return to their substantive post for health reasons is the employee a candidate for ill health retirement application?',
        'In your view, is the employee likely to be covered under the disability provision of the Equality Act 2010? Occupational health can provide guidance only on this question.',
        'Is the employee fit to attend a formal meeting (i.e. disciplinary)?',
        'Other – maximum two extra questions permitted. Please provide detail below. Any additional questions will be chargeable.'
    ];

    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 7: What questions would you want us to ask the employee?
                </h2>
                <div className="flex flex-col items-end">
                    <span className="text-red-500 text-sm font-medium">Required *</span>
                    <span className="text-xs text-gray-600 mt-1">
                        {getSelectedCount()}/3 questions selected
                    </span>
                </div>
            </div>
            <div className="p-4">
                {isAtLimit() && (
                    <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md">
                        <p className="text-sm text-yellow-700">
                            You have selected the maximum of 3 questions. Uncheck a question to select a different one.
                        </p>
                    </div>
                )}
                <div className="space-y-3">
                    {questions.map((question, index) => {
                        const isSelected = hasResponse(question);
                        const isDisabled = !isSelected && isAtLimit();

                        return (
                            <div key={index} className={isDisabled ? 'opacity-50' : ''}>
                                <div className="flex items-start">
                                    <input
                                        type="checkbox"
                                        id={`employee_question_${index}`}
                                        value={question}
                                        className={`mt-1 mr-3 flex-shrink-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        onChange={(e) => handleQuestionUpdate(question, e.target.checked)}
                                        checked={isSelected}
                                        disabled={isDisabled}
                                    />
                                    <label
                                        htmlFor={`employee_question_${index}`}
                                        className={`text-sm leading-relaxed ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}
                                    >
                                        {question}
                                    </label>
                                </div>

                                {question.includes('Other –') && isSelected && (
                                    <div className="mt-2 ml-7">
                                        <textarea
                                            id={`employee_question_other_${index}`}
                                            name={`employee_question_other_${index}`}
                                            rows="3"
                                            className="border rounded-md py-2 px-4 w-full resize-vertical"
                                            placeholder="Please provide details of the additional questions you would like us to ask..."
                                            onChange={(e) => handleQuestionUpdate(question, null, null, 'text_response', e.target.value)}
                                            value={getResponseData(question, 'text_response')}
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EmployeeQuestionsQuestion;